import { useEffect, useRef, useState, useCallback } from "react";
import { Input } from "@/components/ui/input";

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_PLACES_KEY;

let googleScriptLoaded = false;
let googleScriptLoading = false;
const loadCallbacks = [];

function loadGoogleScript() {
  if (googleScriptLoaded) return Promise.resolve();
  if (googleScriptLoading) {
    return new Promise((resolve) => loadCallbacks.push(resolve));
  }
  googleScriptLoading = true;
  return new Promise((resolve, reject) => {
    loadCallbacks.push(resolve);
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places`;
    script.async = true;
    script.onload = () => {
      googleScriptLoaded = true;
      googleScriptLoading = false;
      loadCallbacks.forEach((cb) => cb());
      loadCallbacks.length = 0;
    };
    script.onerror = () => {
      googleScriptLoading = false;
      reject(new Error("Failed to load Google Maps script"));
    };
    document.head.appendChild(script);
  });
}

function parseAddressComponents(place) {
  const result = { street: "", city: "", state: "", zip: "" };
  let streetNumber = "";
  let route = "";

  if (!place.address_components) return result;

  for (const comp of place.address_components) {
    const types = comp.types;
    if (types.includes("street_number")) streetNumber = comp.long_name;
    if (types.includes("route")) route = comp.long_name;
    if (types.includes("locality")) result.city = comp.long_name;
    if (types.includes("sublocality_level_1") && !result.city) result.city = comp.long_name;
    if (types.includes("administrative_area_level_1")) result.state = comp.short_name;
    if (types.includes("postal_code")) result.zip = comp.long_name;
  }

  result.street = streetNumber ? `${streetNumber} ${route}` : route;
  return result;
}

export default function AddressAutocomplete({ street, city, state, zip, onChange, testIdPrefix = "addr" }) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!GOOGLE_API_KEY) return;
    loadGoogleScript().then(() => setReady(true)).catch(() => {});
  }, []);

  const initAutocomplete = useCallback(() => {
    if (!ready || !inputRef.current || autocompleteRef.current) return;
    const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
      types: ["address"],
      componentRestrictions: { country: "us" },
      fields: ["address_components", "formatted_address"],
    });
    ac.addListener("place_changed", () => {
      const place = ac.getPlace();
      if (!place.address_components) return;
      const parsed = parseAddressComponents(place);
      onChange({
        street: parsed.street,
        city: parsed.city,
        state: parsed.state,
        zip: parsed.zip,
      });
    });
    autocompleteRef.current = ac;
  }, [ready, onChange]);

  useEffect(() => {
    initAutocomplete();
  }, [initAutocomplete]);

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <label className="text-sm font-medium">Street Address</label>
        <Input
          ref={inputRef}
          data-testid={`${testIdPrefix}-street`}
          placeholder="Start typing your address..."
          value={street}
          onChange={(e) => onChange({ street: e.target.value, city, state, zip })}
          autoComplete="off"
        />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Input
          data-testid={`${testIdPrefix}-city`}
          placeholder="City"
          value={city}
          onChange={(e) => onChange({ street, city: e.target.value, state, zip })}
        />
        <Input
          data-testid={`${testIdPrefix}-state`}
          placeholder="State"
          value={state}
          onChange={(e) => onChange({ street, city, state: e.target.value, zip })}
        />
        <Input
          data-testid={`${testIdPrefix}-zip`}
          placeholder="Zip"
          value={zip}
          onChange={(e) => onChange({ street, city, state, zip: e.target.value })}
        />
      </div>
    </div>
  );
}
